import type { GridApi, GridOptions, IRowNode } from 'ag-grid-enterprise';
import { get, isEmpty } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCallbackRef, useJsonModal } from '../../../hooks';
import { useConstant } from '../../../hooks/useConstant';
import { EMPTY_OBJECT, logger } from '../../../utils';
import type { BlotterTablePauseProps } from '../BlotterTablePauseButton.types';
import { compileTransactions } from '../helpers';
import { useColumnDefs } from '../useColumnDefs';
import { getParamsFormatted, isGridApiReady, syncNoRowsOverlay } from '../utils';
import { useErrorDetail } from './ErrorDetail';
import type { UseBlotterTable, UseBlotterTableProps } from './types';
import { useBlotterTableEventHandlers } from './useBlotterTableEventHandlers';
import { useBlotterTableGridOptions } from './useBlotterTableGridOptions';
import { useBlotterTableUtilities } from './useBlotterTableUtilities';
import { useInitialGridLoading } from './useInitialGridLoading';

/**
 * Build the arguments for use with the Ava BlotterTable Component
 * @returns
 */
export function useBlotterTable<TRow extends object>({
  dataObservable,
  errorObservable,
  pinnedRowDataPipe,
  pinnedRowDataObs,
  rowID,
  columns,
  flashRows: initialFlashRows,
  density,
  showPinnedRows = true,
  clientLocalFilter,
  handleClickJson,
  context: customBlotterContext,
  quickSearchParams,
  columnDynamicsParams,
  pauseParams,
  customColumnUpdate,
  gridOptions: inputGridOptions,
  getExtraMainMenuItems,
  persistence,
  onBlotterContextChange,
  applyTransactionsSynchronously,
  contextMenuOptions,
  exportOptions,
  settings,
}: UseBlotterTableProps<TRow>): UseBlotterTable<TRow> {
  const [paused, setPaused] = useState(false);
  const [api, setApi] = useState<GridApi>();
  const flashRows = useConstant(initialFlashRows);
  const onGridReady: NonNullable<GridOptions<TRow>['onGridReady']> = useCallback(params => {
    setApi(params.api);
  }, []);

  // Subscribe to data and add to blotter
  const dataState = useConstant(new Set<string>());
  const { clearInitialLoading, controlsInitialLoading, gridOptionsWithLoading, startInitialLoading } =
    useInitialGridLoading({
      dataObservable,
      dataState,
      gridOptions: inputGridOptions,
    });

  useEffect(() => {
    if (isGridApiReady(api) && dataObservable && !paused) {
      startInitialLoading(api);
      const pendingFlashFrames = new Set<number>();
      const scheduleFlashFrame = (callback: FrameRequestCallback) => {
        pendingFlashFrames.add(requestAnimationFrame(callback));
      };
      const subscription = dataObservable.subscribe(next => {
        if (!isGridApiReady(api)) {
          return;
        }
        clearInitialLoading(api);
        if ((isEmpty(next.data) && dataState.size === 0) || rowID == null) {
          api.applyTransactionAsync({ add: [] }, () => syncNoRowsOverlay(api));
          return;
        }
        const transactions = compileTransactions(dataState, next.data, rowID as string, !!next.initial);

        if (!isEmpty(transactions.add) || !isEmpty(transactions.update) || !isEmpty(transactions.remove)) {
          if (applyTransactionsSynchronously) {
            api.applyTransaction(transactions);
            syncNoRowsOverlay(api);
          } else {
            api.applyTransactionAsync(transactions, () => {
              if (!isGridApiReady(api)) {
                return;
              }
              syncNoRowsOverlay(api);
              if (next.initial || !flashRows) {
                return;
              }
              const rowNodeIdInner = rowID;
              if (!rowNodeIdInner) {
                logger.warn('rowID not set, cannot flash rows');
                return;
              }
              // Wait until AG Grid finishes rendering so flashCells does not read eGui from
              // cells that are still in the animation frame queue (TypeError: e.eGui is undefined).
              const flashWhenRendered = () => {
                if (!isGridApiReady(api)) {
                  return;
                }

                if (!api.isAnimationFrameQueueEmpty()) {
                  scheduleFlashFrame(flashWhenRendered);
                  return;
                }

                for (const flash of flashRows) {
                  api.flashCells({
                    rowNodes: transactions[flash]
                      .map((row: TRow) => api.getRowNode(get(row, rowNodeIdInner as string)))
                      .filter(row => row != null) as IRowNode[],
                    flashDuration: 5000,
                    fadeDuration: 2000,
                  });
                }
              };

              scheduleFlashFrame(flashWhenRendered);
            });
          }
        } else {
          syncNoRowsOverlay(api);
        }
      });

      return () => {
        pendingFlashFrames.forEach(id => cancelAnimationFrame(id));
        pendingFlashFrames.clear();
        // Clear out blotter and unsubscribe
        subscription.unsubscribe();
      };
    }
  }, [
    api,
    dataObservable,
    dataState,
    flashRows,
    paused,
    rowID,
    applyTransactionsSynchronously,
    controlsInitialLoading,
    clearInitialLoading,
    startInitialLoading,
  ]);

  // We allow the implementer to either pass a pipe in order to chain of for example an internal observable,
  // Or we allow the implementer to provide their own pinnedRowDataObs. But only one of these can be used
  const pinnedRowDataObsToUse = useMemo(
    () => (dataObservable && pinnedRowDataPipe ? dataObservable.pipe(pinnedRowDataPipe) : pinnedRowDataObs),
    [dataObservable, pinnedRowDataPipe, pinnedRowDataObs]
  );

  // Sub to pinnedRowDataObservable and update pinned top row data when it fires
  useEffect(() => {
    if (isGridApiReady(api) && pinnedRowDataObsToUse && !paused) {
      let timer: ReturnType<typeof setTimeout> | null = null;
      const subscription = pinnedRowDataObsToUse.subscribe(next => {
        // Always clear any previous timer before doing anything new
        if (timer != null) {
          clearTimeout(timer);
        }
        timer = setTimeout(() => {
          if (showPinnedRows) {
            // When updating the pinned top row data (for now we only support one pinned top row),
            // we grab the current row and update its internal data to have the row not "re-mount" on each update.
            // Otherwise, on each update, any open context menu stemming from the pinned top row will close
            const pinnedTopRow = api.getPinnedTopRow(0);
            if (pinnedTopRow) {
              pinnedTopRow.setData(next);
            } else {
              api.setGridOption('pinnedTopRowData', [next]);
            }
          }
        }, 0);
      }); // recommended by aggrid to do setTimeout here

      if (!showPinnedRows) {
        api.setGridOption('pinnedTopRowData', []);
      }

      return () => {
        timer != null && clearTimeout(timer);
        subscription.unsubscribe();
      };
    }
  }, [api, showPinnedRows, pinnedRowDataObsToUse, paused]);

  // Utility functions
  const utilities = useBlotterTableUtilities<TRow>(api, persistence);

  const { handleClickJson: contextMenuHandleClickJson, jsonModal } = useJsonModal();

  const columnDefs = useColumnDefs<TRow>(columns, {
    handleClickJson,
    exportDataAsCSV: utilities.exportDataAsCSV,
    columnDynamicsSpecs: columnDynamicsParams?.columnDynamicsSpecs,
    persistence,
  });

  const resolvedExportOptions = useMemo(() => {
    if (!exportOptions) {
      return undefined;
    }

    return {
      name: exportOptions.name,
      tabLabel: exportOptions.tabLabel,
      baseParams: exportOptions.baseParams,
      csvOptions: {
        exportFunc: utilities.exportDataAsCSV,
        params: exportOptions.csvParams,
      },
      excelOptions: {
        exportFunc: utilities.exportDataAsExcel,
        params: exportOptions.excelParams,
      },
    };
  }, [exportOptions, utilities.exportDataAsCSV, utilities.exportDataAsExcel]);

  // Build GridOptions output
  const resolvedGridOptions = useBlotterTableGridOptions<TRow>({
    gridOptions: gridOptionsWithLoading,
    clientLocalFilter,
    columns,
    columnDefs,
    customBlotterContext,
    density,
    getParamsFormatted,
    onGridReady,
    rowID,
    getExtraMainMenuItems,
    persistence,
    columnDynamicsParams,
    onBlotterContextChange,
    exportOptions: resolvedExportOptions,
    contextMenuOptions,
    contextMenuHandleClickJson,
  });

  // Perform custom column updates if needed based on the current state of the blotter and data
  useEffect(() => {
    const cleanup = customColumnUpdate?.({
      dataObservable,
      autoGroupColumnDef: resolvedGridOptions.autoGroupColumnDef,
      columnDefs,
      api,
    });
    return cleanup;
  }, [api, columnDefs, customColumnUpdate, dataObservable, resolvedGridOptions.autoGroupColumnDef]);

  // Event handlers
  useBlotterTableEventHandlers(
    {
      columns,
      onRowClicked: resolvedGridOptions.onRowClicked,
      persistence,
    },
    api
  );

  const [quickFilterText, setQuickFilterText] = useState('');
  // Use quickSearchParams?.filterText if provided (controlled mode), otherwise use local state.
  const quickFilterString = quickSearchParams?.filterText ?? quickFilterText;
  useEffect(() => {
    if (api) {
      // We allow the implementer to control the filterText if they want. Otherwise, we hold the state.
      api.setGridOption('quickFilterText', quickFilterString);
    }
  }, [api, quickFilterString]);

  const { onFilterTextChange } = quickSearchParams ?? EMPTY_OBJECT;
  const onQuickFilterTextChanged = useCallback(
    (value: string) => {
      setQuickFilterText(value);
      onFilterTextChange?.(value);
    },
    [onFilterTextChange]
  );

  // Whenever clientLocalFilter or the quickFilterText states change, we tell the blotter that filters have changed
  // onFilterChange called in timeout to allow updates to filter function to get picked up correctly by grid
  useEffect(() => {
    const timeout = setTimeout(() => {
      api?.onFilterChanged();
    });
    return () => {
      clearTimeout(timeout);
    };
  }, [api, clientLocalFilter]);

  const pause = useCallback(() => {
    setPaused(true);
  }, []);

  const resume = useCallbackRef(() => {
    api?.setGridOption('rowData', []);
    dataState.clear();
    setPaused(false);
  });

  const pauseProps: BlotterTablePauseProps = useMemo(
    () => ({
      pause,
      paused,
      resume,
      showPauseButton: pauseParams?.showPauseButton ?? false,
    }),
    [pause, paused, resume, pauseParams]
  );

  useEffect(() => {
    api?.resetRowHeights();
  }, [api, resolvedGridOptions?.rowHeight]);

  const errorDetails = useErrorDetail(errorObservable);

  return {
    dataObservable,
    gridOptions: resolvedGridOptions,
    density,
    blotterTableFiltersProps: {
      quickFilterText: quickFilterString,
      onQuickFilterTextChanged,
      ...pauseProps,
    },
    ...utilities,
    errorDetails,
    exportOptions,
    dialogShowJsonModal: jsonModal,
    settings,
  };
}
